import React, { useContext, useEffect, useState, memo } from 'react';
import { Box } from '@mui/material';
import { ThemeContext } from '../App';

/**
 * A layout component that applies weather-themed styling to its children based on ThemeContext
 * This component should be used to wrap page content
 */
const WeatherThemedLayout = ({ children }) => {
  const { 
    mode, 
    weatherThemeEnabled, 
    weatherCondition,
    weatherThemeMode,
    selectedWeatherTheme
  } = useContext(ThemeContext);
  
  // Add force update state to ensure the component re-renders when settings change
  const [forceUpdate, setForceUpdate] = useState(0);

  // Determine the effective weather condition
  const effectiveCondition = weatherThemeMode === 'manual' && weatherThemeEnabled
    ? selectedWeatherTheme
    : weatherCondition || 'clear';
  
  // Listen for localStorage changes to update styling
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (['weatherCondition', 'weatherThemeEnabled', 'weatherThemeMode', 'selectedWeatherTheme'].includes(e.key)) {
        // Force a component update when weather-related settings change
        setForceUpdate(prev => prev + 1);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Function to get weather-themed background color with opacity
  const getWeatherThemedBackground = (opacity = 0.05) => {
    if (!weatherThemeEnabled) return 'transparent';
    
    const isDark = mode === 'dark';
    let color = '';
    
    switch(effectiveCondition.toLowerCase()) {
      case 'clear':
        color = isDark ? '#ff9800' : '#ffc107'; // Orange/amber for clear/sunny
        break;
      case 'clouds':
        color = isDark ? '#455a64' : '#b0bec5'; // Keep gray for clouds
        break;
      case 'rain':
      case 'drizzle':
        color = isDark ? '#00796b' : '#26a69a'; // Teal for rain instead of blue
        break;
      case 'snow':
        color = isDark ? '#cfd8dc' : '#eceff1'; // Light blue-gray for snow
        break;
      case 'thunderstorm':
        color = isDark ? '#673ab7' : '#9c27b0'; // Purple for thunderstorm
        break;
      case 'spring':
        color = isDark ? '#66bb6a' : '#81c784'; // Green for spring growth
        break;
      case 'summer':
        color = isDark ? '#26c6da' : '#00bcd4'; // Cyan/teal for summer water
        break;
      case 'autumn':
        color = isDark ? '#ff7043' : '#ff5722'; // Deep orange for autumn leaves
        break;
      case 'winter':
        color = isDark ? '#90caf9' : '#2196f3'; // Blue for winter ice
        break;
      default:
        color = isDark ? '#424242' : '#f5f5f5';
    }
    
    // Convert hex to rgba with opacity
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Helper to create semi-transparent gradient colors (same as in DetachableClock)
  const makeTransparent = (color, opacity) => {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  };

  // Get drawer background and color based on weather condition
  const getDrawerStyle = () => {
    if (!weatherThemeEnabled) return '';
    
    const isDark = mode === 'dark';
    // Use the same opacity values as DetachableClock
    const bgOpacity = isDark ? 0.7 : 0.5;
    const endOpacity = isDark ? 0.6 : 0.4;
    let background;
    
    switch(effectiveCondition.toLowerCase()) {
      case 'clear':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#ff6f00', bgOpacity)}, ${makeTransparent('#ff9800', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#ffd54f', bgOpacity)}, ${makeTransparent('#ffb300', endOpacity)})`;
        break;
      case 'clouds':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#263238', bgOpacity)}, ${makeTransparent('#37474f', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#b0bec5', bgOpacity)}, ${makeTransparent('#78909c', endOpacity)})`;
        break;
      case 'rain':
      case 'drizzle':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#00695c', bgOpacity)}, ${makeTransparent('#00897b', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#80deea', bgOpacity)}, ${makeTransparent('#00acc1', endOpacity)})`;
        break;
      case 'snow':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#546e7a', bgOpacity)}, ${makeTransparent('#78909c', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#e1f5fe', bgOpacity)}, ${makeTransparent('#b3e5fc', endOpacity)})`;
        break;
      case 'thunderstorm':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#4a148c', bgOpacity)}, ${makeTransparent('#6a1b9a', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#9575cd', bgOpacity)}, ${makeTransparent('#673ab7', endOpacity)})`;
        break;
      case 'spring':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#43a047', bgOpacity)}, ${makeTransparent('#66bb6a', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#66bb6a', bgOpacity)}, ${makeTransparent('#81c784', endOpacity)})`;
        break;
      case 'summer':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#0097a7', bgOpacity)}, ${makeTransparent('#26c6da', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#00bcd4', bgOpacity)}, ${makeTransparent('#4dd0e1', endOpacity)})`;
        break;
      case 'autumn':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#e64a19', bgOpacity)}, ${makeTransparent('#ff7043', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#ff5722', bgOpacity)}, ${makeTransparent('#ff8a65', endOpacity)})`;
        break;
      case 'winter':
        background = isDark
          ? `linear-gradient(145deg, ${makeTransparent('#1976d2', bgOpacity)}, ${makeTransparent('#42a5f5', endOpacity)})`
          : `linear-gradient(145deg, ${makeTransparent('#2196f3', bgOpacity)}, ${makeTransparent('#64b5f6', endOpacity)})`;
        break;
      default:
        background = isDark 
          ? 'linear-gradient(145deg, rgba(30, 30, 30, 0.7) 0%, rgba(42, 42, 42, 0.6) 100%)'
          : 'linear-gradient(145deg, rgba(255, 255, 255, 0.5) 0%, rgba(248, 249, 250, 0.4) 100%)';
    }
    
    return `.MuiDrawer-paper {
      background: ${background} !important;
      color: ${isDark ? '#ffffff' : '#212121'};
    }`;
  };

  // Get app bar style based on weather condition
  const getAppBarStyle = () => {
    if (!weatherThemeEnabled) return '';
    
    const isDark = mode === 'dark';
    // Use the same opacity values as DetachableClock but slightly more opaque for better visibility
    const bgOpacity = isDark ? 0.8 : 0.6; 
    const endOpacity = isDark ? 0.7 : 0.5;
    let background;
    
    switch(effectiveCondition.toLowerCase()) {
      case 'clear':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#ff6f00', bgOpacity)}, ${makeTransparent('#ff9800', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#ffd54f', bgOpacity)}, ${makeTransparent('#ffb300', endOpacity)})`;
        break;
      case 'clouds':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#263238', bgOpacity)}, ${makeTransparent('#37474f', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#b0bec5', bgOpacity)}, ${makeTransparent('#78909c', endOpacity)})`;
        break;
      case 'rain':
      case 'drizzle':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#00695c', bgOpacity)}, ${makeTransparent('#00897b', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#80deea', bgOpacity)}, ${makeTransparent('#00acc1', endOpacity)})`;
        break;
      case 'snow':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#546e7a', bgOpacity)}, ${makeTransparent('#78909c', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#e1f5fe', bgOpacity)}, ${makeTransparent('#b3e5fc', endOpacity)})`;
        break;
      case 'thunderstorm':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#4a148c', bgOpacity)}, ${makeTransparent('#6a1b9a', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#9575cd', bgOpacity)}, ${makeTransparent('#673ab7', endOpacity)})`;
        break;
      case 'spring':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#43a047', bgOpacity)}, ${makeTransparent('#66bb6a', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#66bb6a', bgOpacity)}, ${makeTransparent('#81c784', endOpacity)})`;
        break;
      case 'summer':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#0097a7', bgOpacity)}, ${makeTransparent('#26c6da', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#00bcd4', bgOpacity)}, ${makeTransparent('#4dd0e1', endOpacity)})`;
        break;
      case 'autumn':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#e64a19', bgOpacity)}, ${makeTransparent('#ff7043', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#ff5722', bgOpacity)}, ${makeTransparent('#ff8a65', endOpacity)})`;
        break;
      case 'winter':
        background = isDark
          ? `linear-gradient(to right, ${makeTransparent('#1976d2', bgOpacity)}, ${makeTransparent('#42a5f5', endOpacity)})`
          : `linear-gradient(to right, ${makeTransparent('#2196f3', bgOpacity)}, ${makeTransparent('#64b5f6', endOpacity)})`;
        break;
      default:
        background = isDark
          ? 'linear-gradient(to right, rgba(30, 30, 30, 0.8), rgba(42, 42, 42, 0.7))'
          : 'linear-gradient(to right, rgba(255, 255, 255, 0.6), rgba(248, 249, 250, 0.5))';
    }
    
    return `.MuiAppBar-root {
      background: ${background} !important;
    }`;
  };

  // Return only children without any additional styling
  // This eliminates any layout issues from this component
  return (
    <>
      {weatherThemeEnabled && (
        <style>
          {`
          /* Only apply subtle accent colors to buttons and other interactive elements */
          .MuiButton-containedPrimary:hover {
            box-shadow: 0 0 8px ${getWeatherThemedBackground(0.8)};
          }
          
          /* Target navigation drawer and app bar for all weather themes */
          ${getDrawerStyle()}
          
          ${getAppBarStyle()}
          
          /* Target Settings page components */
          .MuiAccordion-root {
            background: ${mode === 'dark' 
              ? `linear-gradient(145deg, ${getWeatherThemedBackground(0.1)} 0%, ${getWeatherThemedBackground(0.15)} 100%)` 
              : `linear-gradient(145deg, ${getWeatherThemedBackground(0.05)} 0%, ${getWeatherThemedBackground(0.1)} 100%)`} !important;
          }
          
          /* Match settings card background with dashboard cards */
          .settings-card {
            background: ${weatherThemeEnabled
              ? mode === 'dark'
                ? `linear-gradient(145deg, ${getWeatherThemedBackground(0.7)} 0%, ${getWeatherThemedBackground(0.6)} 100%)`
                : `linear-gradient(145deg, ${getWeatherThemedBackground(0.5)} 0%, ${getWeatherThemedBackground(0.4)} 100%)`
              : mode === 'light'
                ? 'linear-gradient(145deg, rgba(144, 202, 249, 0.5) 0%, rgba(33, 150, 243, 0.7) 100%)'
                : 'linear-gradient(145deg, rgba(50, 50, 50, 1) 0%, rgba(66, 66, 66, 1) 100%)'} !important;
          }
          `}
        </style>
      )}
      {children}
    </>
  );
};

// Memoized: this wraps {children} for every single page (only usage:
// Navigation.js). When weatherThemeEnabled is on, it rebuilds a template-
// literal <style> string (two multi-branch switch statements plus hex-to-
// rgba conversions) and re-inserts a fresh <style> tag targeting broad,
// page-wide selectors (.MuiButton-containedPrimary:hover, .MuiDrawer-paper,
// .MuiAppBar-root, ...) - forcing the browser to invalidate/recompute
// matched styles across the whole page - on every render. Navigation.js
// owns several of its own local useStates for everyday interactions
// (collapsing the sidebar, opening the logout dialog, opening Support)
// that have nothing to do with weather theming but still re-render
// WeatherThemedLayout on every one of them, since it had no memo boundary.
// Only effective now that Navigation.js memoizes the children it passes in
// (see weatherLayoutChildren) - otherwise the children prop's reference
// still changes on every Navigation render regardless of this memo.
export default memo(WeatherThemedLayout); 